import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/css/admin/document/DataAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";
import { getDataList, deleteData } from "../../../api/dataApi";
import { getStoredAdminInfo } from "../AdminAuth";

import SearchD from "../../../assets/img/Search_D.svg";
import SearchF from "../../../assets/img/Search_F.svg";

const normalizeCategory = (value) => {
  if (value === "audit-committee") return "audit";
  if (value === "central-committee") return "central";
  if (value === "student-council") return "stdcouncil";

  return value || "stdcouncil";
};

const getRouteCategoryFromCategory = (category) => {
  if (category === "audit") return "audit-committee";
  if (category === "central") return "central-committee";
  if (category === "stdcouncil") return "stdcouncil";

  return category;
};

const getDataArray = (result) => {
  const candidates = [
    result?.content,
    result?.items,
    result?.list,
    result?.data?.content,
    result?.data?.items,
    result?.data?.list,
    result?.data?.data,
    result?.data,
  ];

  const found = candidates.find((item) => Array.isArray(item));

  return found || [];
};

function DataAdmin({
  category: categoryProp,
  successMessage,
  onClearSuccessMessage,
  onWrite,
  onEdit,
}) {
  const params = useParams();
  const navigate = useNavigate();

  const { isCc, isAudit } = getStoredAdminInfo();
  const isCcDataPage = isCc || isAudit;

  const rawCategory = categoryProp || params.category || "stdcouncil";
  const category = normalizeCategory(rawCategory);
  const routeCategory = getRouteCategoryFromCategory(category);

  const [list, setList] = useState({
    stdcouncil: [],
    central: [],
    audit: [],
    etc: [],
    delegate: [],
  });

  const [isLoading, setIsLoading] = useState(false);

  const [activeDataType, setActiveDataType] = useState("rule");

  const [dataFilterType, setDataFilterType] = useState("all");
  const [dataFilterOpen, setDataFilterOpen] = useState(false);

  const [sortType, setSortType] = useState("title");
  const [filterOpen, setFilterOpen] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState("");

  const [appliedSearchType, setAppliedSearchType] = useState("title");
  const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const searchIcon = searchKeyword.trim() ? SearchF : SearchD;

  const pageTitle = useMemo(() => {
    if (category === "stdcouncil") return "총학생회 자료";
    if (category === "central") return "중앙운영위원회 자료";
    if (category === "audit") return "감사위원회 자료";
    if (category === "etc") return "기타 자료";
    if (category === "delegate") return "대의원 활동 신청";

    return "자료";
  }, [category]);

  const categoryLogName = pageTitle;

  useEffect(() => {
    if (category === "stdcouncil") {
      setActiveDataType("rule");
    } else {
      setActiveDataType("data");
    }

    setDataFilterType("all");
    setDataFilterOpen(false);

    setSortType("title");
    setAppliedSearchType("title");

    setSearchKeyword("");
    setAppliedSearchKeyword("");

    setFilterOpen(false);
    setCurrentPage(1);
  }, [category]);

  const getDataFilterLabel = () => {
    if (dataFilterType === "all") return "전체";

    if (activeDataType === "rule") {
      if (dataFilterType === "rule") return "회칙";
      if (dataFilterType === "bylaw") return "세칙";
    }

    if (activeDataType === "form") {
      if (dataFilterType === "accounting") return "회계";
      if (dataFilterType === "event") return "행사/사업";
      if (dataFilterType === "admin") return "행정/운영";
      if (dataFilterType === "etc") return "기타";
    }

    return "전체";
  };

  const getSortLabel = () => {
    if (sortType === "title") return "제목";
    if (sortType === "content") return "내용";

    return "제목";
  };

  const currentList = useMemo(() => {
    const baseList = list[category] || [];

    const filteredByType =
      category === "stdcouncil"
        ? baseList.filter((item) => {
            if (activeDataType === "rule") {
              return item.dataType === "rule" || item.dataType === "bylaw";
            }

            if (activeDataType === "form") {
              return item.dataType === "form";
            }

            return true;
          })
        : baseList;

    const filteredByDataFilter = filteredByType.filter((item) => {
      if (dataFilterType === "all") return true;

      if (activeDataType === "form") {
        return item.formType === dataFilterType;
      }

      return item.dataType === dataFilterType;
    });

    const sorted = [...filteredByDataFilter];

    sorted.sort((a, b) => {
      const idA = Number(a.id || 0);
      const idB = Number(b.id || 0);

      return idB - idA;
    });

    return sorted;
  }, [list, category, activeDataType, dataFilterType]);

  const totalPages = Math.ceil(currentList.length / itemsPerPage);

  const safeCurrentPage =
    totalPages === 0
      ? 1
      : Math.min(Math.max(currentPage, 1), totalPages);

  const pagedList = currentList.slice(
    (safeCurrentPage - 1) * itemsPerPage,
    safeCurrentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [category, activeDataType, dataFilterType]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    const fetchDataList = async () => {
      try {
        setIsLoading(true);

        const result = await getDataList({
          category,
          activeDataType,
          dataFilterType,
          searchCategory:
            appliedSearchType === "content" ? "CONTENT" : "TITLE",
          keyWord: appliedSearchKeyword,
          page: 0,
          size: 100,
        });

        console.log(`${categoryLogName} 목록 조회 성공:`, result);

        const serverList = getDataArray(result);

        const getMappedDataType = (item) => {
          const rawItemCategory =
            item.category || item.docCategory || item.listCategory;

          if (category !== "stdcouncil") {
            return {
              dataType: item.dataType || item.docType || "data",
              formType: "",
            };
          }

          if (
            rawItemCategory === "CONSTITUTION" ||
            rawItemCategory === "회칙"
          ) {
            return {
              dataType: "rule",
              formType: "",
            };
          }

          if (
            rawItemCategory === "BYLAW" ||
            rawItemCategory === "세칙"
          ) {
            return {
              dataType: "bylaw",
              formType: "",
            };
          }

          if (
            rawItemCategory === "BUDGET" ||
            rawItemCategory === "회계"
          ) {
            return {
              dataType: "form",
              formType: "accounting",
            };
          }

          if (
            rawItemCategory === "EVENT" ||
            rawItemCategory === "행사/사업"
          ) {
            return {
              dataType: "form",
              formType: "event",
            };
          }

          if (
            rawItemCategory === "ADMINISTRATION" ||
            rawItemCategory === "행정/운영"
          ) {
            return {
              dataType: "form",
              formType: "admin",
            };
          }

          if (
            rawItemCategory === "ETC" ||
            rawItemCategory === "기타"
          ) {
            return {
              dataType: "form",
              formType: "etc",
            };
          }

          return {
            dataType: "data",
            formType: "",
          };
        };

        const formattedList = serverList.map((item) => {
          const mapped = getMappedDataType(item);

          const id =
            item.id ??
            item.docId ??
            item.auditDocId ??
            item.councilDocId ??
            item.executiveDocId ??
            item.etcDocId ??
            item.certificateDocId;

          return {
            id,
            dataType: mapped.dataType,
            formType: mapped.formType,
            title: item.title || "",
            writer:
              item.writer ||
              item.userName ||
              item.createdByName ||
              item.createdBy ||
              "관리자",
            date:
              item.date ||
              item.createdAt?.slice(0, 10).replaceAll("-", ".") ||
              "",
            content: item.content || "",
            fileName: item.fileName || "",
          };
        });

        setList((prev) => ({
          ...prev,
          [category]: formattedList,
        }));
      } catch (error) {
        console.error(`${categoryLogName} 목록 조회 실패:`, error);

        setList((prev) => ({
          ...prev,
          [category]: [],
        }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDataList();
  }, [
    category,
    activeDataType,
    dataFilterType,
    categoryLogName,
    appliedSearchType,
    appliedSearchKeyword,
  ]);

  const handleSearch = () => {
    setAppliedSearchType(sortType);
    setAppliedSearchKeyword(searchKeyword.trim());
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("삭제하시겠습니까?");

    if (!confirmDelete) return;

    try {
      await deleteData(category, id);

      setList((prev) => ({
        ...prev,
        [category]: (prev[category] || []).filter(
          (item) => item.id !== id
        ),
      }));

      alert("삭제되었습니다.");
    } catch (error) {
      console.error("자료 삭제 실패:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleWriteMove = () => {
    const state = {
      dataType: "data",
      formType: "",
    };

    if (category === "stdcouncil") {
      if (activeDataType === "form") {
        state.dataType = "form";
        state.formType =
          dataFilterType !== "all" ? dataFilterType : "accounting";
      } else {
        state.dataType =
          dataFilterType === "bylaw" ? "bylaw" : "rule";
        state.formType = "";
      }
    }

    if (onWrite) {
      onWrite(state);
      return;
    }

    navigate(
      `/stdcounciladmin/adminhome/data/${routeCategory}/write`,
      {
        state,
      }
    );
  };

  const handleEditMove = (item) => {
    if (onEdit) {
      onEdit(item);
      return;
    }

    navigate(
      `/stdcounciladmin/adminhome/data/${routeCategory}/edit/${item.id}`,
      {
        state: item,
      }
    );
  };

  const handleSelectDataFilter = (value) => {
    setDataFilterType(value);
    setDataFilterOpen(false);
    setCurrentPage(1);
  };

  const handleSelectFilter = (value) => {
    setSortType(value);
    setFilterOpen(false);
    setCurrentPage(1);
  };

  const dataAdminContent = (
    <div className="data-admin-page admin-scroll-x">
      <div className="data-admin-inner">
        <h2 className="data-admin-title">{pageTitle}</h2>

        {successMessage && (
          <div className="data-success-alert">
            <div className="data-success-icon">✓</div>

            <p>{successMessage}</p>

            <button
              type="button"
              className="data-success-close"
              onClick={onClearSuccessMessage}
            >
              ×
            </button>
          </div>
        )}

        {category === "stdcouncil" && (
          <div className="data-tab-area">
            <button
              type="button"
              className={`data-tab-btn ${
                activeDataType === "rule" ? "active" : ""
              }`}
              onClick={() => {
                setActiveDataType("rule");
                setDataFilterType("all");
                setDataFilterOpen(false);
              }}
            >
              회칙/세칙
            </button>

            <button
              type="button"
              className={`data-tab-btn ${
                activeDataType === "form" ? "active" : ""
              }`}
              onClick={() => {
                setActiveDataType("form");
                setDataFilterType("all");
                setDataFilterOpen(false);
              }}
            >
              게시판
            </button>
          </div>
        )}

        <div className="data-admin-toolbar">
          <button
            className="data-add-btn"
            onClick={handleWriteMove}
            type="button"
          >
            +
          </button>

          <div className="data-admin-filter">
            {category === "stdcouncil" && (
              <div className="data-filter-left">
                <div className="data-custom-filter">
                  <button
                    type="button"
                    className={`data-filter-toggle ${
                      dataFilterOpen ? "is-open" : ""
                    }`}
                    onClick={() => {
                      setDataFilterOpen((prev) => !prev);
                      setFilterOpen(false);
                    }}
                  >
                    <span>{getDataFilterLabel()}</span>
                    <span
                      className="data-filter-arrow"
                      aria-hidden="true"
                    />
                  </button>

                  {dataFilterOpen && (
                    <div className="data-filter-menu">
                      <button
                        type="button"
                        onClick={() =>
                          handleSelectDataFilter("all")
                        }
                      >
                        전체
                      </button>

                      {activeDataType === "rule" && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleSelectDataFilter("rule")
                            }
                          >
                            회칙
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              handleSelectDataFilter("bylaw")
                            }
                          >
                            세칙
                          </button>
                        </>
                      )}

                      {activeDataType === "form" && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleSelectDataFilter("accounting")
                            }
                          >
                            회계
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              handleSelectDataFilter("event")
                            }
                          >
                            행사/사업
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              handleSelectDataFilter("admin")
                            }
                          >
                            행정/운영
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              handleSelectDataFilter("etc")
                            }
                          >
                            기타
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="data-filter-right">
              <div className="data-custom-filter">
                <button
                  type="button"
                  className={`data-filter-toggle ${
                    filterOpen ? "is-open" : ""
                  }`}
                  onClick={() => {
                    setFilterOpen((prev) => !prev);
                    setDataFilterOpen(false);
                  }}
                >
                  <span>{getSortLabel()}</span>
                  <span
                    className="data-filter-arrow"
                    aria-hidden="true"
                  />
                </button>

                {filterOpen && (
                  <div className="data-filter-menu">
                    <button
                      type="button"
                      onClick={() =>
                        handleSelectFilter("title")
                      }
                    >
                      제목
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleSelectFilter("content")
                      }
                    >
                      내용
                    </button>
                  </div>
                )}
              </div>

              <div className="data-search-box">
                <input
                  type="text"
                  placeholder="검색어를 입력하세요."
                  value={searchKeyword}
                  onChange={(e) =>
                    setSearchKeyword(e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />

                <button
                  type="button"
                  className="data-search-btn"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  <img src={searchIcon} alt="검색" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="data-table-wrap">
          <div className="data-table-header">
            <div>No</div>
            <div>제목</div>
            <div>작성자</div>
            <div>작성일</div>
            <div>관리</div>
          </div>

          {isLoading ? (
            <div className="data-table-row">
              <div>...</div>
              <div>불러오는 중입니다.</div>
              <div>-</div>
              <div>-</div>
              <div className="data-table-actions" />
            </div>
          ) : pagedList.length > 0 ? (
            pagedList.map((item, index) => {
              const displayNo = String(
                currentList.length -
                  ((safeCurrentPage - 1) * itemsPerPage + index)
              ).padStart(3, "0");

              return (
                <div
                  key={item.id ?? index}
                  className="data-table-row"
                >
                  <div>{displayNo}</div>

                  <div>{item.title}</div>
                  <div>{item.writer}</div>
                  <div>{item.date}</div>

                  <div className="data-table-actions">
                    <button
                      className="edit"
                      onClick={() => handleEditMove(item)}
                      type="button"
                    >
                      수정
                    </button>

                    <button
                      className="delete"
                      onClick={() => handleDelete(item.id)}
                      type="button"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="data-table-empty-row">
              등록된 자료가 없습니다.
            </div>
          )}
        </div>

        <div className="data-pagination">
          <button
            type="button"
            onClick={() => setCurrentPage(1)}
            disabled={safeCurrentPage === 1}
          >
            ‹‹
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentPage((prev) => Math.max(prev - 1, 1))
            }
            disabled={safeCurrentPage === 1}
          >
            ‹
          </button>

          {Array.from(
            { length: totalPages || 1 },
            (_, index) => index + 1
          ).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              className={
                safeCurrentPage === pageNumber ? "active" : ""
              }
              onClick={() => setCurrentPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(prev + 1, totalPages || 1)
              )
            }
            disabled={safeCurrentPage === (totalPages || 1)}
          >
            ›
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentPage(totalPages || 1)
            }
            disabled={safeCurrentPage === (totalPages || 1)}
          >
            ››
          </button>
        </div>
      </div>
    </div>
  );

  return isCcDataPage ? (
    <div className="cc-data-admin-shell">
      {dataAdminContent}
    </div>
  ) : (
    dataAdminContent
  );
}

export default DataAdmin;